import styles from './Button.module.css';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    loading?: boolean;
    /**
     * Shown next to the spinner while `loading`. Without it the spinner replaces
     * the label, which is right for an action the customer just pressed but not
     * for a state they are waiting on (the success screen's receipt, SWRN-2271).
     */
    loadingText?: string;
}

const renderContent = (
    loading: boolean | undefined,
    loadingText: string | undefined,
    children: React.ReactNode,
): React.ReactNode => {
    if (!loading) return children;
    if (!loadingText) return <span className={styles.spinner} />;
    return (
        <>
            <span className={styles.spinner} />
            <span className={styles.loadingText}>{loadingText}</span>
        </>
    );
};

const Button = ({ className, loading, loadingText, disabled, children, ...props }: ButtonProps): React.JSX.Element => (
    <button
        className={`${styles.button}${className ? ` ${className}` : ''}`}
        disabled={loading || disabled}
        {...props}
    >
        {renderContent(loading, loadingText, children)}
    </button>
);

const SmallButton = ({ className, loading, loadingText, disabled, children, ...props }: ButtonProps): React.JSX.Element => (
    <button
        className={`${styles.button} ${styles.smallButton}${className ? ` ${className}` : ''}`}
        disabled={loading || disabled}
        {...props}
    >
        {renderContent(loading, loadingText, children)}
    </button>
);

const SmallPrimaryButton = ({ className, loading, loadingText, disabled, children, ...props }: ButtonProps): React.JSX.Element => (
    <button
        className={`${styles.button} ${styles.smallButton} ${styles.smallPrimaryButton}${className ? ` ${className}` : ''}`}
        disabled={loading || disabled}
        {...props}
    >
        {renderContent(loading, loadingText, children)}
    </button>
);

export { Button, SmallButton, SmallPrimaryButton };
